import {
  differenceInHours,
  format,
  formatDistanceToNowStrict,
  intervalToDuration,
} from "date-fns";
import { enCA } from "date-fns/locale";

import Config from "../config";
import { AuthStorage } from "../local-storage";

export const logout = () => {
  if (Config.isDev) {
    const authStorage = new AuthStorage();
    authStorage.clear();
  }
  window.location.href = "/logout";
};

export function assertIsError(error: unknown): asserts error is Error {
  if (!(error instanceof Error)) {
    throw error;
  }
}

type EmptyValue = "" | null | undefined;
export function isEmptyValue(value: unknown): value is EmptyValue {
  return value === "" || value === null || value === undefined;
}

/**
 * Type guard to check if an object has a specific key.
 * Useful for narrowing unknown types safely.
 */
export function hasKey<K extends string>(
  o: unknown,
  k: K,
): o is { [_ in K]: unknown } {
  return typeof o === "object" && o !== null && k in o;
}

export const normalizePathname = (pathname: string): string =>
  pathname.replace(/\/$/, "");

// Date formatter to format date consistently. We can use this to localize as well eventually.
// (param) timestamp:  an ISO 8601 formatted timestamp as 2025-05-14T19:55:41.585585Z
export const formatDateTimeString = (timestamp: string) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Invalid Date";

  const timeString = format(date, "p", { locale: enCA });
  const dateString = format(date, "P", { locale: enCA });

  return `${dateString}, ${timeString}`;
};

export const formatDistance = (timestamp: string) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Invalid Date";

  const fromNowString = formatDistanceToNowStrict(date, {
    addSuffix: true,
  });
  return fromNowString;
};

export const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Invalid Date";

  if (differenceInHours(new Date(), date) < 24) {
    return formatDistance(timestamp);
  }
  return formatDateTimeString(timestamp);
};

// Helper function to get duration parts
const getDurationParts = (durationInMs: number) => {
  // Validate input to prevent invalid date errors
  if (
    durationInMs === null ||
    durationInMs === undefined ||
    isNaN(durationInMs) ||
    durationInMs < 0
  ) {
    return [];
  }

  // Use epoch 0 as the reference point to avoid DST-related calculation issues
  const duration = intervalToDuration({
    start: 0,
    end: durationInMs,
  });

  const parts = [];
  if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
  if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
  if (duration.minutes && duration.minutes > 0)
    parts.push(`${duration.minutes}m`);
  if (duration.seconds && duration.seconds > 0)
    parts.push(`${duration.seconds}s`);

  return parts;
};

// Duration formatter to format duration like 1d 2h 33m 45s, usually in reference to job duration
// This does not currently localize the duration, but it could be added in the future
export const formatDuration = (durationInMs: number) => {
  const parts = getDurationParts(durationInMs);
  return parts.join(" ");
};

// Same as formatDuration, but with a max character length and uses ">" to indicate truncation
export const formatShortDuration = (
  durationInMs: number,
  maxCharacters: number = 6,
) => {
  const parts = getDurationParts(durationInMs);
  if (parts.length === 0) {
    return "";
  }

  let formattedText = parts.join(" ");

  if (formattedText.length > maxCharacters) {
    const truncatedParts = [...parts];
    while (
      truncatedParts.join(" ").length > maxCharacters - 1 &&
      truncatedParts.length > 1
    ) {
      truncatedParts.pop(); // Remove the last (smallest) time unit
    }
    formattedText = ">" + truncatedParts.join(" ");
  }

  return formattedText;
};

export function getDateRangeForQuery(
  dateRange: "isToday" | "isWeek",
): string[] {
  const endDate = new Date();
  let startDate: Date;

  // Set start date based on dateRange
  if (dateRange === "isToday") {
    startDate = new Date(endDate);
    startDate.setHours(0, 0, 0, 0); // Start of today
  } else {
    // thisWeek
    startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7);
    startDate.setHours(0, 0, 0, 0); // Start of day 7 days ago
  }

  const startDateISO = startDate.toISOString();
  const endDateISO = endDate.toISOString();

  return [startDateISO, endDateISO];
}

/**
 * Checks if a date range represents "today" (start date is today).
 * Used to determine if the "Today" quick filter pill should be highlighted.
 */
export function isDateRangeToday(
  range: (string | null)[] | undefined,
): boolean {
  if (!range || range.length === 0 || !range[0]) {
    return false;
  }

  const startDate = new Date(range[0]);
  const today = new Date();

  return (
    startDate.getDate() === today.getDate() &&
    startDate.getMonth() === today.getMonth() &&
    startDate.getFullYear() === today.getFullYear()
  );
}

/**
 * Checks if a date range represents "this week" (start date is ~7 days ago).
 * Used to determine if the "This Week" quick filter pill should be highlighted.
 */
export function isDateRangeThisWeek(
  range: (string | null)[] | undefined,
): boolean {
  if (!range || range.length < 2 || !range[0] || !range[1]) {
    return false;
  }

  const startDate = new Date(range[0]);
  const endDate = new Date(range[1]);
  const today = new Date();

  // Check if end date is today
  const endIsToday =
    endDate.getDate() === today.getDate() &&
    endDate.getMonth() === today.getMonth() &&
    endDate.getFullYear() === today.getFullYear();

  // Check if start date is ~7 days ago (allow some tolerance for time differences)
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const startIsSevenDaysAgo =
    startDate.getDate() === sevenDaysAgo.getDate() &&
    startDate.getMonth() === sevenDaysAgo.getMonth() &&
    startDate.getFullYear() === sevenDaysAgo.getFullYear();

  return endIsToday && startIsSevenDaysAgo;
}

export function parseAccountFromUrn(urn?: string): string | undefined {
  if (!urn) return undefined;
  const parts = urn.split(":");
  const idx = parts.indexOf("account");
  return idx !== -1 ? parts[idx + 1] : undefined;
}
